import React, { useLayoutEffect, useRef } from 'react';

// Animate the viewport once between its actual sizes. The content has its own
// natural-height box, so observing it never feeds animation frames back into
// the target height. An interrupted toggle starts from the visible frame.
export default function AnimatedDisclosure({
  open,
  collapsedHeight = 0,
  children,
  className = '',
  contentClassName = '',
  onContentResize,
  style,
  ...props
}) {
  const viewport = useRef(null);
  const content = useRef(null);
  const animation = useRef(null);
  const initialized = useRef(false);
  const onResize = useRef(onContentResize);
  onResize.current = onContentResize;

  useLayoutEffect(() => {
    const element = viewport.current;
    const inner = content.current;
    let lastTarget;
    const clearAnimation = () => {
      if (!animation.current) return;
      animation.current.onfinish = null;
      animation.current.cancel();
      animation.current = null;
    };
    const update = (toggled = false) => {
      const from = element.getBoundingClientRect().height;
      const fullHeight = Math.ceil(inner.getBoundingClientRect().height || inner.scrollHeight);
      onResize.current?.(fullHeight);

      let target = fullHeight;
      if (!open) {
        if (typeof collapsedHeight === 'number') target = Math.min(fullHeight, collapsedHeight);
        else {
          // Resolve CSS lengths on the unanimated inner box. Reading them on
          // the viewport would return an in-flight animation's height instead.
          const previousHeight = inner.style.height;
          inner.style.height = collapsedHeight;
          const limit = parseFloat(window.getComputedStyle(inner).height) || 0;
          inner.style.height = previousHeight;
          target = Math.min(fullHeight, limit);
        }
      }
      if (!toggled && target === lastTarget) return;
      lastTarget = target;
      const shouldAnimate = initialized.current && (toggled || (open && animation.current));
      clearAnimation();
      element.style.height = open ? 'auto' : `${target}px`;
      const overflow = open || (collapsedHeight !== 0 && target >= fullHeight) ? 'visible' : 'hidden';
      element.style.overflow = overflow;
      initialized.current = true;

      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (!shouldAnimate || reducedMotion || !element.animate || Math.abs(from - target) < 1) return;

      element.style.overflow = 'hidden';
      const current = element.animate(
        [{ height: `${from}px` }, { height: `${target}px` }],
        { duration: 240, easing: 'cubic-bezier(.2,.7,.2,1)' }
      );
      animation.current = current;
      current.onfinish = () => {
        if (animation.current !== current) return;
        animation.current = null;
        element.style.overflow = overflow;
      };
    };

    update(true);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(() => update()) : null;
    observer?.observe(inner);
    const resized = () => update();
    window.addEventListener('resize', resized);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resized);
    };
  }, [open, collapsedHeight]);

  useLayoutEffect(() => () => {
    if (animation.current) {
      animation.current.onfinish = null;
      animation.current.cancel();
    }
  }, []);

  const fullyHidden = !open && collapsedHeight === 0;
  return <div
    {...props}
    ref={viewport}
    className={`animated-disclosure ${className}`}
    aria-hidden={fullyHidden || undefined}
    inert={fullyHidden || undefined}
    style={{ ...style, display: 'block', minHeight: 0, maxHeight: 'none', transition: 'none', animation: 'none' }}
  >
    <div ref={content} className={`disclosure-content ${contentClassName}`} style={{ display: 'flow-root', minHeight: 0 }}>
      {children}
    </div>
  </div>;
}
